import { createTheme, ThemeProvider } from '@mui/material/styles'
// Ensina o tema a conhecer o slot MuiDataGrid — sem isto, as overrides do grid são
// erro de compilação, embora funcionem em execução.
import '@mui/x-data-grid/themeAugmentation'

// Inter self-hospedada (Figma: fonte única). Os woff2 vivem em src/assets/fonts e o
// Vite os serve com hash — nada de CDN (CSP font-src 'self').
import inter400 from '@/assets/fonts/inter-400.woff2'
import inter500 from '@/assets/fonts/inter-500.woff2'
import inter600 from '@/assets/fonts/inter-600.woff2'
import inter700 from '@/assets/fonts/inter-700.woff2'

// ─── Originei Design System — alinhado ao Figma (frame Foundations 4:9 / 4:59) ─
//   • Tipografia:     Inter (única), self-hospedada em src/assets/fonts
//   • Orange:         #F15A24 (primária)
//   • Ink:            #17191D (sidebar e texto)
//   • Page:           #F6F7F9 · Card: #FFFFFF
//   • Success/Warning/Danger: #16A34A / #D98B1C / #DC2626
//   • Escala:         h1 30 · seção 18 · card 15 · corpo 14 · legenda 12
//   • Radius:         10 (padrão), 14 (cards) — mantidos
//   • Shadows:        flat, exceto hover de card (0 8px 32px rgba(0,0,0,.10))
//   • Transições:     0.15–0.2s ease
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Tokens do Figma. Fonte única de verdade: o `createTheme` abaixo e as variáveis CSS
 * semânticas do `:root` (o pedaço "grátis" do White Label, gate C3) derivam daqui.
 */
export const tokens = {
  brandPrimary: '#F15A24',
  brandPrimaryDark: '#D64A17',
  brandPrimaryLight: '#F4794D',
  /** Tint de hover (fundo de linha/botão outlined sob o cursor). */
  brandPrimarySoft: '#FDEFE9',
  /** Tint de seleção (menu/autocomplete selecionados, chip primário). */
  brandPrimarySoftStrong: '#FCE2D7',
  brandInk: '#17191D',
  surfacePage: '#F6F7F9',
  surfaceCard: '#FFFFFF',
  statusSuccess: '#16A34A',
  statusWarning: '#D98B1C',
  statusDanger: '#DC2626',
  textSecondary: '#6B7280',
  divider: '#E5E5E7',
} as const

// Variáveis CSS semânticas — exatamente estas sete, nada além (contrato do A01):
// um tema de cliente futuro trocaria só estes valores.
const cssVariaveisSemanticas = `
        :root {
          --brand-primary: ${tokens.brandPrimary};
          --brand-ink: ${tokens.brandInk};
          --surface-page: ${tokens.surfacePage};
          --surface-card: ${tokens.surfaceCard};
          --status-success: ${tokens.statusSuccess};
          --status-warning: ${tokens.statusWarning};
          --status-danger: ${tokens.statusDanger};
        }`

// Tons derivados (claro/escuro/fundos suaves) — não são tokens semânticos, só apoio visual.
const SUCCESS_SOFT = '#E8F6EE'
const SUCCESS_DARK = '#15803D'
const WARNING_SOFT = '#FBF1E0'
const WARNING_DARK = '#A8690F'
const DANGER_SOFT = '#FBEBEB'
const DANGER_DARK = '#B91C1C'

const FONT =
  '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif'

export const theme = createTheme({
  palette: {
    primary: {
      main: tokens.brandPrimary,
      light: tokens.brandPrimaryLight,
      dark: tokens.brandPrimaryDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#2ECC71',
      light: '#58D68D',
      dark: '#27AE60',
      contrastText: '#FFFFFF',
    },
    background: {
      default: tokens.surfacePage,
      paper: tokens.surfaceCard,
    },
    error:   { main: tokens.statusDanger,  light: '#EF4444', dark: DANGER_DARK },
    warning: { main: tokens.statusWarning, light: '#F0A93E', dark: WARNING_DARK },
    success: { main: tokens.statusSuccess, light: '#22C55E', dark: SUCCESS_DARK },
    info:    { main: '#2980B9', light: '#5499C7', dark: '#1F618D' },
    text: {
      primary: tokens.brandInk,
      secondary: tokens.textSecondary,
      disabled: '#BDBDBD',
    },
    divider: tokens.divider,
  },
  typography: {
    fontFamily: FONT,
    // Escala do Figma: página 30 · seção 18 (h4) · título de card 15 (h6) ·
    // corpo 14 (body1) · legenda 12 (caption).
    h1: { fontSize: '1.875rem', fontWeight: 700, lineHeight: 1.2,  letterSpacing: '-0.02em' },
    h2: { fontSize: '1.5rem',   fontWeight: 700, lineHeight: 1.2,  letterSpacing: '-0.02em' },
    h3: { fontSize: '1.25rem',  fontWeight: 700, lineHeight: 1.25, letterSpacing: '-0.02em' },
    h4: { fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.3,  letterSpacing: '-0.02em' },
    h5: { fontSize: '1rem',     fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.02em' },
    h6: { fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.35, letterSpacing: '-0.02em' },
    body1:     { fontSize: '0.875rem', lineHeight: 1.5, letterSpacing: '-0.02em' },
    body2:     { fontSize: '0.8125rem', lineHeight: 1.5, letterSpacing: '-0.02em' },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '-0.02em' },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '-0.02em' },
    button:    { fontSize: '0.875rem',  fontWeight: 600, textTransform: 'none', letterSpacing: '-0.01em' },
    caption:   { fontSize: '0.75rem',   lineHeight: 1.5, letterSpacing: '-0.01em' },
    overline:  { fontSize: '0.6875rem', fontWeight: 700, lineHeight: 1.6, letterSpacing: '0.05em', textTransform: 'uppercase' },
  },
  shape: { borderRadius: 10 },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        /* Inter self-hospedada (src/assets/fonts, empacotada pelo Vite) */
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 400;
          font-display: swap;
          src: url('${inter400}') format('woff2');
        }
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 500;
          font-display: swap;
          src: url('${inter500}') format('woff2');
        }
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 600;
          font-display: swap;
          src: url('${inter600}') format('woff2');
        }
        @font-face {
          font-family: 'Inter';
          font-style: normal;
          font-weight: 700;
          font-display: swap;
          src: url('${inter700}') format('woff2');
        }
        ${cssVariaveisSemanticas}
        *, *::before, *::after { box-sizing: border-box; }
        html, body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          letter-spacing: -0.02em;
        }
        body {
          background-color: ${tokens.surfacePage};
          color: ${tokens.brandInk};
          font-family: ${FONT};
        }
        h1, h2, h3, h4, h5, h6 { font-family: ${FONT}; letter-spacing: -0.02em; }
        ::-webkit-scrollbar { width: 10px; height: 10px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #D0D0D5; border-radius: 6px; }
        ::-webkit-scrollbar-thumb:hover { background: #B0B0B8; }
      `,
    },

    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: 'none',
          fontWeight: 600,
          letterSpacing: '-0.01em',
          padding: '10px 18px',
          boxShadow: 'none',
          transition: 'background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          backgroundColor: tokens.brandPrimary,
          '&:hover': { backgroundColor: tokens.brandPrimaryDark },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': { borderWidth: '1.5px', backgroundColor: tokens.brandPrimarySoft },
        },
        outlinedPrimary: {
          color: tokens.brandPrimary,
          borderColor: tokens.brandPrimary,
          '&:hover': { borderColor: tokens.brandPrimaryDark, backgroundColor: tokens.brandPrimarySoft },
        },
        text: { '&:hover': { backgroundColor: tokens.brandPrimarySoft } },
        sizeLarge:  { padding: '12px 24px', fontSize: '0.9375rem', borderRadius: 12 },
        sizeSmall:  { padding: '7px 14px',  fontSize: '0.8125rem', borderRadius: 8 },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          transition: 'background-color 0.15s ease',
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 14,
          boxShadow: 'none',
          border: `1px solid ${tokens.divider}`,
          backgroundColor: tokens.surfaceCard,
          transition: 'border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            borderColor: tokens.divider,
            boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
          },
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: { padding: 24, '&:last-child': { paddingBottom: 24 } },
      },
    },

    MuiPaper: {
      styleOverrides: {
        root: { backgroundImage: 'none', borderRadius: 14 },
        elevation0: { boxShadow: 'none', border: `1px solid ${tokens.divider}` },
        elevation1: { boxShadow: 'none', border: `1px solid ${tokens.divider}` },
        elevation2: { boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: `1px solid ${tokens.divider}` },
        elevation4: { boxShadow: '0 4px 18px rgba(0,0,0,0.08)' },
        elevation8: { boxShadow: '0 8px 32px rgba(0,0,0,0.10)' },
      },
    },

    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: `1px solid ${tokens.divider}`,
          borderRadius: 14,
          boxShadow: 'none',
          backgroundColor: tokens.surfaceCard,
          '& .MuiDataGrid-cell': {
            borderColor: tokens.surfacePage,
            letterSpacing: '-0.02em',
            fontSize: '0.875rem',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F7F7F8',
            borderColor: tokens.divider,
            fontWeight: 700,
            letterSpacing: '0.04em',
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            color: tokens.textSecondary,
          },
          '& .MuiDataGrid-row:hover': { backgroundColor: tokens.brandPrimarySoft },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus': { outline: 'none' },
        },
      },
    },

    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 10,
            backgroundColor: tokens.surfaceCard,
            '&:hover fieldset': { borderColor: tokens.brandPrimary },
            '&.Mui-focused fieldset': { borderColor: tokens.brandPrimary, borderWidth: 2, boxShadow: '0 0 0 3px rgba(241, 90, 36, 0.12)' },
          },
          '& label.Mui-focused': { color: tokens.brandPrimary },
          '& .MuiInputBase-input': { letterSpacing: '-0.02em' },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: tokens.surfaceCard,
          '& fieldset': { borderColor: tokens.divider, borderWidth: '1.5px' },
          '&:hover fieldset': { borderColor: tokens.brandPrimary },
          '&.Mui-focused fieldset': { borderColor: tokens.brandPrimary, borderWidth: 2 },
        },
        input: { letterSpacing: '-0.02em' },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          letterSpacing: '-0.01em',
          '&.Mui-focused': { color: tokens.brandPrimary },
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
          fontSize: '0.75rem',
          letterSpacing: '-0.01em',
          height: 24,
          paddingLeft: 4,
          paddingRight: 4,
          // `colorDefault` deixou de ser slot de override do Chip no MUI v6; o estilo
          // continua o mesmo, aplicado pela classe.
          '&.MuiChip-colorDefault': { backgroundColor: '#F2F3F4', color: tokens.textSecondary },
        },
        sizeSmall: { height: 22, fontSize: '0.6875rem' },
        colorPrimary: { backgroundColor: tokens.brandPrimarySoftStrong, color: tokens.brandPrimary },
        colorSuccess: { backgroundColor: SUCCESS_SOFT, color: tokens.statusSuccess },
        colorWarning: { backgroundColor: WARNING_SOFT, color: tokens.statusWarning },
        colorError:   { backgroundColor: DANGER_SOFT, color: tokens.statusDanger },
        colorInfo:    { backgroundColor: '#EBF2F8', color: '#2980B9' },
        outlined: { borderWidth: '1.5px' },
      },
    },

    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surfaceCard,
          color: tokens.brandInk,
          boxShadow: 'none',
          borderBottom: `1px solid ${tokens.divider}`,
        },
      },
    },

    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.brandInk,
          color: '#FFFFFF',
          border: 'none',
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          letterSpacing: '-0.01em',
          borderLeft: '3px solid transparent',
          transition: 'background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease',
          '&.Mui-selected': {
            backgroundColor: 'transparent',
            color: '#FFFFFF',
            borderLeftColor: tokens.brandPrimary,
            '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
            '& .MuiListItemIcon-root': { color: tokens.brandPrimary },
            '& .MuiListItemText-primary': { color: '#FFFFFF', fontWeight: 600 },
          },
          '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
        },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: tokens.divider } },
    },

    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 14, boxShadow: '0 8px 32px rgba(0,0,0,0.16)' },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
          marginTop: 6,
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          border: `1px solid ${tokens.divider}`,
        },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          letterSpacing: '-0.02em',
          fontSize: '0.875rem',
          '&:hover': { backgroundColor: tokens.brandPrimarySoft },
          '&.Mui-selected': { backgroundColor: tokens.brandPrimarySoftStrong, color: tokens.brandPrimary },
        },
      },
    },

    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 42, borderBottom: `1px solid ${tokens.divider}` },
        indicator: { backgroundColor: tokens.brandPrimary, height: 2, borderRadius: '2px 2px 0 0' },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.875rem',
          letterSpacing: '-0.02em',
          minHeight: 42,
          padding: '8px 16px',
          color: tokens.textSecondary,
          '&.Mui-selected': { color: tokens.brandPrimary, fontWeight: 700 },
        },
      },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F7F7F8',
            fontWeight: 700,
            fontSize: '0.6875rem',
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
            color: tokens.textSecondary,
            borderBottom: `1px solid ${tokens.divider}`,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: tokens.surfacePage,
          letterSpacing: '-0.02em',
          fontSize: '0.875rem',
          padding: '14px 16px',
        },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: tokens.brandPrimarySoft } },
      },
    },

    MuiStepIcon: {
      styleOverrides: {
        root: {
          '&.Mui-active':    { color: tokens.brandPrimary },
          '&.Mui-completed': { color: tokens.statusSuccess },
        },
      },
    },

    MuiSlider: {
      styleOverrides: {
        root:  { color: tokens.brandPrimary },
        thumb: {
          '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 8px rgba(241, 90, 36, 0.14)' },
        },
      },
    },

    MuiCheckbox: {
      styleOverrides: { root: { '&.Mui-checked': { color: tokens.brandPrimary } } },
    },

    MuiRadio: {
      styleOverrides: { root: { '&.Mui-checked': { color: tokens.brandPrimary } } },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: tokens.brandPrimary,
            '& + .MuiSwitch-track': { backgroundColor: tokens.brandPrimary },
          },
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { borderRadius: 4, backgroundColor: tokens.brandPrimarySoftStrong, height: 6 },
        bar:  { borderRadius: 4, backgroundColor: tokens.brandPrimary },
      },
    },

    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: 8,
          fontSize: '0.75rem',
          letterSpacing: '-0.01em',
          backgroundColor: tokens.brandInk,
          fontWeight: 500,
        },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: 10, letterSpacing: '-0.02em', border: '1px solid' },
        standardInfo:    { backgroundColor: '#EBF2F8', color: '#1F618D', borderColor: '#D4E6F1', '& .MuiAlert-icon': { color: '#2980B9' } },
        standardSuccess: { backgroundColor: SUCCESS_SOFT, color: SUCCESS_DARK, borderColor: '#CDEBD9', '& .MuiAlert-icon': { color: tokens.statusSuccess } },
        standardWarning: { backgroundColor: WARNING_SOFT, color: WARNING_DARK, borderColor: '#F3E2C0', '& .MuiAlert-icon': { color: tokens.statusWarning } },
        standardError:   { backgroundColor: DANGER_SOFT, color: DANGER_DARK, borderColor: '#F2C7C7', '& .MuiAlert-icon': { color: tokens.statusDanger } },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        option: {
          letterSpacing: '-0.02em',
          '&[aria-selected="true"]': { backgroundColor: `${tokens.brandPrimarySoftStrong} !important`, color: tokens.brandPrimary },
          '&:hover': { backgroundColor: tokens.brandPrimarySoft },
        },
      },
    },
  },
})

export { ThemeProvider }
